import jsPsychHtmlKeyboardResponse from "@jspsych/plugin-html-keyboard-response";
import CustomInstructionsResponsePlugin from "../../customPlugin/customInstructions";

import { audio, div, h1, image } from "../../lib/markup/tags";
import { instructionImage } from "../../lib/utils";
import { buildQuizInstructions } from "./instructions";
import { buildFreeSortQuizTrial } from "./freeSortQuiz";

const CELEBRATE_AUDIO = "assets/audio/CG01_10_Celebrate_V1.wav";

/**
 * Displays the mission feedback screen as an instructions trial
 * @param {Object} jsPsych The jsPsych instance being used to run the task
 * @param {Score} score The score instance used to keep track of the score for the mission
 * @param {String} missionMinScore Per mission minimum score to continue
 * @param {String} missionMaxCrash Per mission max crashes allowed
 * @param {string} backgroundImage The background image of the feedback screen
 */
export function buildFeedbackTrial(
  jsPsych,
  score,
  missionMinScore,
  missionMaxCrash,
  backgroundImage
) {
  // First stage: Feedback trial
  const feedbackTrial = {
    type: CustomInstructionsResponsePlugin,
    data: { name: "feedbackScreen" },
    key_forward: "Space",
    key_backward: "KeyZ",
    pages: () => {
      const { missionReward, missionPackages, missionCrashes } = score.getScore();
      return [
        div(
          audio({ src: CELEBRATE_AUDIO, autoplay: true, style: "hidden" }) +
          instructionImage(backgroundImage) +
          div(
            div(
              h1("Total Rewards:", { class: "rewardLabel" }) +
              div(h1(missionReward) + image({ src: "assets/images/hud/star.svg" }), {
                class: "rewardValue",
              }),
              { class: "rewardContainer" }
            ) +
            div(
              h1("Total Packages:", { class: "rewardLabel" }) +
              div(
                h1(missionPackages) +
                image({ src: "assets/images/packages/package_medium.svg" }),
                { class: "rewardValue" }
              ),
              { class: "rewardContainer" }
            ) +
            div(
              h1("Total Crashes:", { class: "rewardLabel" }) +
              div(
                h1(missionCrashes) +
                image({ src: "assets/images/driver/collisionDriver.svg" }),
                { class: "rewardValue" }
              ),
              { class: "rewardContainer" }
            ),
            { class: "feedbackContainer" }
          ),
          { class: "feedbackScreen" }
        ),
      ];
    },
    on_finish: (data) => score.assignScore(data),
  };
  //Optional end experiment trial if reward less than threshold or crashes over threshold
  const conditionalEndTrial = {
    timeline: [
      {
        type: jsPsychHtmlKeyboardResponse,
        stimulus: "<p>You did not reach minimal performance, Game Over</p>",
        choices: "NO_KEYS",
        trial_duration: 3000,
        on_finish: () => {
          jsPsych.endExperiment(
            "Game over due to low score. Thank you for participating. You can close the browser now."
          );
        },
      },
    ],
    conditional_function: () => {
      const { missionReward, missionCrashes } = score.getScore();
      return missionReward < missionMinScore || missionCrashes > missionMaxCrash;
    },
  };

  // Combine trials in a timeline
  return [feedbackTrial, conditionalEndTrial];
}

/**
 * Builds the procedure for the first free-sort quiz
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Score} score The Score instance used to keep track of the user's score within the mission
 */
export function buildQuizOneProcedure(jsPsych, score) {
  return {
    timeline: [
      // Pre-instructions
      buildQuizInstructions(jsPsych, score, ["assets/images/postgame/postgame 2.svg"]),
      // First quiz
      buildFreeSortQuizTrial(jsPsych, score, "assets/images/postgame/packageSortBackground.svg", [
        "assets/images/packages/package_medium.svg",
        "assets/images/packages/package_high.svg",
        "assets/images/packages/package_low.svg",
      ]),
      // TODO: uncomment out if answer key wanted after quiz1
      // // Post-instructions (answer key)
      // buildQuizInstructions(jsPsych, score, ["assets/images/postgame/postgame 7.svg"], { showOverlayNavText: false }),
    ],
  };
}

/**
 * Builds the procedure for the first free-sort quiz
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Score} score The Score instance used to keep track of the user's score within the mission
 */
export function buildQuizTwoProcedure(jsPsych, score) {
  return {
    timeline: [
      buildQuizInstructions(jsPsych, score, ["assets/images/postgame/postgame 8.svg"]),
      // Quiz
      buildFreeSortQuizTrial(jsPsych, score, "assets/images/postgame/carSortBackground.svg", [
        "assets/images/targetCars/target_flat_blue_sad.svg",
        "assets/images/targetCars/target_flat_green_neutral.svg",
        "assets/images/targetCars/target_flat_yellow_happy.svg",
        "assets/images/targetCars/target_round_blue_sad.svg",
        "assets/images/targetCars/target_round_green_neutral.svg",
        "assets/images/targetCars/target_round_yellow_happy.svg",
        "assets/images/targetCars/target_tall_blue_sad.svg",
        "assets/images/targetCars/target_tall_green_neutral.svg",
        "assets/images/targetCars/target_tall_yellow_happy.svg",
      ]),
      // Post-instructions (answer key)
      // TODO: uncomment out if answer key wanted after quiz2
      // buildQuizInstructions(jsPsych, score, ["assets/images/postgame/postgame 13.svg"], { showOverlayNavText: false }),
    ],
  };
}
